import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, RefreshCw, MessageCircle, Phone, Mail, ArrowLeft, ExternalLink, Star, ChevronUp, ChevronDown, X, PauseCircle } from 'lucide-react';
import Header from '../components/Header';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { authService } from '../services/authService';
import { operacoesService } from '../services/operacoesService';
import socketService from '../services/socketService';
import { toast } from '../hooks/use-toast';
import { getFileUrl, getInitials } from '../utils/dashboardHelpers';
import { PainelOperacionalResponse, PainelTicket, AtendenteCarga, CanalTicket, PeriodoPainel } from '../types/operacoes';

type Urgencia = 'ok' | 'warn' | 'crit';
type Bucket = 'pendente' | 'atendimento' | 'aguardando';
type Tom = 'default' | 'warn' | 'accent' | 'ok' | 'crit';

const THRESH_PENDENTE = { warn: 10 * 60 * 1000, crit: 20 * 60 * 1000 };
const THRESH_ATENDIMENTO = { warn: 15 * 60 * 1000, crit: 30 * 60 * 1000 };

const CANAL_LABEL: Record<CanalTicket, string> = {
  EVOLUTION: 'WhatsApp',
  UAZAPI: 'WhatsApp',
  META: 'Meta',
  DESCONHECIDO: 'Canal'
};

function urgencia(ms: number, thresh: { warn: number; crit: number }): Urgencia {
  if (ms >= thresh.crit) return 'crit';
  if (ms >= thresh.warn) return 'warn';
  return 'ok';
}

// Desconta a pausa "aguardando ação do cliente" em andamento (cronômetro congelado);
// pausas já encerradas o backend desconta adiantando statusAlteradoEm.
function decorrido(ticket: PainelTicket): number {
  const fim = ticket.aguardandoClienteDesde ? new Date(ticket.aguardandoClienteDesde).getTime() : Date.now();
  return fim - new Date(ticket.statusAlteradoEm).getTime();
}

// Ticket pausado aguardando o cliente não conta como atrasado
function urgenciaTicket(ticket: PainelTicket, thresh: { warn: number; crit: number }): Urgencia {
  return ticket.aguardandoClienteDesde ? 'ok' : urgencia(decorrido(ticket), thresh);
}

const SeloAguardandoCliente: React.FC = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300 px-1.5 py-0.5 rounded flex-none">
    <PauseCircle className="h-3 w-3" />
    Aguardando cliente
  </span>
);

function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}`;
  if (h > 0) return `${h}:${pad(m)}:${pad(sec)}`;
  return `${m}:${pad(sec)}`;
}

function fmtDuracaoSegundos(seg: number | null): string {
  if (seg === null || seg === undefined) return '—';
  return fmtElapsed(seg * 1000);
}

function fmtAvaliacao(nota: number | null): string {
  if (nota === null || nota === undefined) return '—';
  return nota.toFixed(1);
}

const PERIODO_LABEL: Record<PeriodoPainel, string> = {
  hoje: 'Hoje',
  '7dias': '7 dias',
  '30dias': '30 dias',
  '90dias': '90 dias'
};

const URGENCY_BORDER: Record<Urgencia, string> = {
  ok: 'border-l-emerald-500',
  warn: 'border-l-amber-500',
  crit: 'border-l-red-500'
};

const URGENCY_TEXT: Record<Urgencia, string> = {
  ok: 'text-emerald-600 dark:text-emerald-400',
  warn: 'text-amber-600 dark:text-amber-400',
  crit: 'text-red-600 dark:text-red-400'
};

const TOM_TEXT: Record<Tom, string> = {
  default: 'text-gray-900 dark:text-gray-100',
  warn: 'text-amber-600 dark:text-amber-400',
  accent: 'text-sky-600 dark:text-sky-400',
  ok: 'text-emerald-600 dark:text-emerald-400',
  crit: 'text-red-600 dark:text-red-400'
};

const STATUS_ATENDENTE_LABEL: Record<string, string> = {
  online: 'Online',
  offline: 'Offline',
  ausente: 'Ausente'
};

const STATUS_ATENDENTE_DOT: Record<string, string> = {
  online: 'bg-emerald-500',
  offline: 'bg-zinc-400 dark:bg-zinc-600',
  ausente: 'bg-amber-500'
};

const STATUS_ORDEM: Record<string, number> = { online: 0, ausente: 1, offline: 2 };

type ColunaEquipe = 'nome' | 'chats' | 'pendentes' | 'resolvidos' | 'total' | 'nota' | 'tme' | 'tma';

const totalAtendente = (a: AtendenteCarga) => a.pendentesAtribuidos + a.ticketsEmAtendimento + a.resolvidosPeriodo;

const VALOR_COLUNA: Record<Exclude<ColunaEquipe, 'nome'>, (a: AtendenteCarga) => number | null> = {
  chats: (a) => a.ticketsEmAtendimento,
  pendentes: (a) => a.pendentesAtribuidos,
  resolvidos: (a) => a.resolvidosPeriodo,
  total: totalAtendente,
  nota: (a) => a.avaliacaoMedia,
  tme: (a) => a.tmeSegundos,
  tma: (a) => a.tmaSegundos
};

const PainelOperacoesPage: React.FC = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isGestor = user?.role === 'Admin' || user?.role === 'SuperAdmin';

  const [data, setData] = useState<PainelOperacionalResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filtroAtendenteId, setFiltroAtendenteId] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<PeriodoPainel>('hoje');
  const [, setTick] = useState(0);
  const [ordemEquipe, setOrdemEquipe] = useState<{ coluna: ColunaEquipe; desc: boolean }>({ coluna: 'chats', desc: true });

  const [ticketSelecionado, setTicketSelecionado] = useState<PainelTicket | null>(null);
  const [bucketSelecionado, setBucketSelecionado] = useState<Bucket | null>(null);

  useEffect(() => {
    if (user && !isGestor) {
      navigate('/dashboard');
    }
  }, [user, isGestor, navigate]);

  const fetchPainel = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    else setRefreshing(true);
    try {
      const resposta = await operacoesService.getPainel(periodo);
      setData(resposta);
    } catch (error) {
      console.error('Erro ao carregar painel operacional:', error);
      if (!silencioso) {
        toast({ title: 'Não foi possível carregar o painel.', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [periodo]);

  useEffect(() => {
    if (isGestor) fetchPainel();
  }, [isGestor, fetchPainel]);

  // Cronômetros ao vivo
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Atualização quase em tempo real via socket (reaproveita a conexão já usada no Dashboard)
  const fetchPainelRef = useRef(fetchPainel);
  fetchPainelRef.current = fetchPainel;

  useEffect(() => {
    if (!isGestor) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    let cancelado = false;
    const handleAtualizacao = () => fetchPainelRef.current(true);

    socketService.connect(token).then(() => {
      if (cancelado) return;
      socketService.on('chatListUpdated', handleAtualizacao);
      socketService.on('conversa_atualizada', handleAtualizacao);
      socketService.on('ticket_aguardando_cliente', handleAtualizacao);
      socketService.on('conversa_removida', handleAtualizacao);
    }).catch((err) => console.error('Erro ao conectar socket no painel operacional:', err));

    return () => {
      cancelado = true;
      socketService.off('chatListUpdated', handleAtualizacao);
      socketService.off('conversa_atualizada', handleAtualizacao);
      socketService.off('ticket_aguardando_cliente', handleAtualizacao);
      socketService.off('conversa_removida', handleAtualizacao);
    };
  }, [isGestor]);

  const filtrar = useCallback((tickets: PainelTicket[]) => {
    let lista = tickets;
    if (filtroAtendenteId) {
      lista = lista.filter((t) => t.atendente?.id === filtroAtendenteId);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      lista = lista.filter((t) =>
        (t.contato?.nome || '').toLowerCase().includes(q) ||
        (t.contato?.telefone || '').toLowerCase().includes(q) ||
        (t.assunto || '').toLowerCase().includes(q) ||
        t.protocolo.toLowerCase().includes(q)
      );
    }
    return lista;
  }, [filtroAtendenteId, search]);

  const pendentes = useMemo(() => filtrar(data?.pendentes || []), [data, filtrar]);
  const atendimento = useMemo(() => filtrar(data?.atendimento || []), [data, filtrar]);
  const aguardando = useMemo(() => filtrar(data?.aguardando || []), [data, filtrar]);

  // KPIs ao vivo usam o total da empresa, sem os filtros do quadro.
  // Calculados a cada render porque dependem do relógio (tick de 1s).
  const todosPendentes = data?.pendentes || [];
  const todosAtendimento = data?.atendimento || [];
  const esperaMediaMs = todosPendentes.length
    ? todosPendentes.reduce((acc, t) => acc + decorrido(t), 0) / todosPendentes.length
    : null;
  const pendentesCriticos = todosPendentes.filter((t) => urgenciaTicket(t, THRESH_PENDENTE) === 'crit').length;
  const atendimentoCriticos = todosAtendimento.filter((t) => urgenciaTicket(t, THRESH_ATENDIMENTO) === 'crit').length;

  const atendentesOnline = useMemo(
    () => (data?.atendentes || []).filter((a) => a.status === 'online').length,
    [data]
  );

  const equipeOrdenada = useMemo(() => {
    const { coluna, desc } = ordemEquipe;
    const sinal = desc ? -1 : 1;
    return [...(data?.atendentes || [])].sort((a, b) => {
      if (coluna === 'nome') return sinal * a.nome.localeCompare(b.nome, 'pt-BR');
      const va = VALOR_COLUNA[coluna](a);
      const vb = VALOR_COLUNA[coluna](b);
      // Sem valor (—) vai sempre para o fim
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va !== vb) return sinal * (va - vb);
      return (STATUS_ORDEM[a.status || 'offline'] ?? 2) - (STATUS_ORDEM[b.status || 'offline'] ?? 2);
    });
  }, [data, ordemEquipe]);

  const ordenarEquipe = (coluna: ColunaEquipe) => {
    setOrdemEquipe((prev) =>
      prev.coluna === coluna ? { coluna, desc: !prev.desc } : { coluna, desc: coluna !== 'nome' && coluna !== 'tme' && coluna !== 'tma' }
    );
  };

  const atendenteFiltrado = data?.atendentes.find((a) => a.id === filtroAtendenteId) || null;

  const abrirTicket = (ticket: PainelTicket, bucket: Bucket) => {
    setTicketSelecionado(ticket);
    setBucketSelecionado(bucket);
  };
  const fecharDrawer = () => {
    setTicketSelecionado(null);
    setBucketSelecionado(null);
  };

  const abrirConversa = (ticket: PainelTicket) => {
    navigate(`/dashboard?ticketId=${ticket.id}`);
  };

  if (!isGestor) return null;

  const periodoLabel = PERIODO_LABEL[periodo];

  return (
    <div className="flex flex-col h-[100dvh] bg-gray-100 dark:bg-slate-900 overflow-hidden">
      <Header titulo="Painel de Operações" user={user} />

      {/* Em telas grandes a página ocupa exatamente a altura da janela e cada coluna rola sozinha */}
      <div className="flex-1 min-h-0 overflow-y-auto xl:overflow-hidden p-3 md:p-4 flex flex-col gap-3">
        {/* Barra de controles: voltar, período, busca, legenda e atualizar numa única linha */}
        <div className="flex flex-wrap items-center gap-2 flex-none">
          <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 h-8 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>

          <div
            className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-0.5"
            title="Período das métricas de desempenho. O quadro de tickets é sempre ao vivo."
          >
            {(Object.keys(PERIODO_LABEL) as PeriodoPainel[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriodo(p)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  periodo === p
                    ? 'bg-sky-600 text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
              >
                {PERIODO_LABEL[p]}
              </button>
            ))}
          </div>

          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por cliente, telefone ou protocolo…"
              className="pl-8 h-8 text-sm bg-white dark:bg-slate-800"
            />
          </div>

          {atendenteFiltrado && (
            <Badge variant="secondary" className="cursor-pointer gap-1 h-7" onClick={() => setFiltroAtendenteId(null)}>
              {atendenteFiltrado.nome.split(' ')[0]}
              <X className="h-3 w-3" />
            </Badge>
          )}

          <div className="flex items-center gap-3 ml-auto text-[11px] text-gray-400">
            <LegendaCor cor="bg-emerald-500" label="Normal" />
            <LegendaCor cor="bg-amber-500" label="Atenção" />
            <LegendaCor cor="bg-red-500" label="Parado há muito tempo" />
            <LegendaCor cor="bg-sky-400" label="Aguardando cliente" />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title="Atualizar"
              onClick={() => fetchPainel(true)}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* KPIs: ao vivo (destaque) + período, compactos */}
            <div className="grid grid-cols-1 xl:grid-cols-[4fr_8fr] gap-3 flex-none">
              <GrupoKpi titulo="Ao vivo" live>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <KpiTile
                    destaque
                    label="Pendentes"
                    value={todosPendentes.length}
                    tone={pendentesCriticos > 0 ? 'crit' : 'warn'}
                    sub={pendentesCriticos > 0 ? `${pendentesCriticos} parado${pendentesCriticos > 1 ? 's' : ''} há muito tempo` : 'aguardando técnico'}
                  />
                  <KpiTile
                    destaque
                    label="Atendendo"
                    value={todosAtendimento.length}
                    tone={atendimentoCriticos > 0 ? 'crit' : 'accent'}
                    sub={atendimentoCriticos > 0 ? `${atendimentoCriticos} parado${atendimentoCriticos > 1 ? 's' : ''} há muito tempo` : 'com técnico'}
                  />
                  <KpiTile
                    destaque
                    label="Espera média"
                    value={esperaMediaMs !== null ? fmtElapsed(esperaMediaMs) : '—'}
                    tone={esperaMediaMs !== null ? (urgencia(esperaMediaMs, THRESH_PENDENTE) === 'ok' ? 'default' : urgencia(esperaMediaMs, THRESH_PENDENTE) === 'warn' ? 'warn' : 'crit') : 'default'}
                    sub="dos pendentes"
                  />
                  <KpiTile
                    destaque
                    label="Online"
                    value={`${atendentesOnline}/${data?.atendentes.length ?? 0}`}
                    tone="ok"
                    sub="atendentes"
                  />
                </div>
              </GrupoKpi>

              <GrupoKpi titulo={`Período · ${periodoLabel}`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2">
                  <KpiTile label="Atendimentos" value={data?.totalAtendimentosPeriodo ?? 0} tone="default" sub="total" />
                  <KpiTile label="Resolvidos" value={data?.resolvidosPeriodo ?? 0} tone="ok" sub="finalizados" />
                  <KpiTile label="Ativas" value={data?.demandaAtivaPeriodo ?? 0} tone="accent" sub="empresa iniciou" />
                  <KpiTile label="Receptivas" value={data?.demandaReceptivaPeriodo ?? 0} tone="accent" sub="cliente iniciou" />
                  <KpiTile label="TME" value={fmtDuracaoSegundos(data?.tmeSegundos ?? null)} tone="default" sub="tempo médio espera" />
                  <KpiTile label="TMA" value={fmtDuracaoSegundos(data?.tmaSegundos ?? null)} tone="default" sub="tempo médio atend." />
                  <KpiTile label="Avaliação" value={fmtAvaliacao(data?.avaliacaoMediaPeriodo ?? null)} tone="warn" sub="nota média" />
                  <KpiTile label="Novos contatos" value={data?.novosContatosPeriodo ?? 0} tone="ok" sub="cadastrados" />
                </div>
              </GrupoKpi>
            </div>

            {/* Quadro: pendentes | em atendimento | aguardando retorno | equipe — preenche o resto da tela */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.7fr)] gap-3 xl:flex-1 xl:min-h-0">
              <ColunaTickets
                titulo="Pendentes"
                subtitulo="Sem técnico · mais tempo parado no topo"
                tickets={pendentes}
                bucket="pendente"
                thresh={THRESH_PENDENTE}
                onSelect={abrirTicket}
              />
              <ColunaTickets
                titulo="Em atendimento"
                subtitulo="Com técnico · mais tempo em atendimento no topo"
                tickets={atendimento}
                bucket="atendimento"
                thresh={THRESH_ATENDIMENTO}
                onSelect={abrirTicket}
              />
              <ColunaTickets
                titulo="Aguardando retorno"
                subtitulo="Aguardando retorno do contato · mais antigos no topo"
                tickets={aguardando}
                bucket="aguardando"
                thresh={THRESH_ATENDIMENTO}
                onSelect={abrirTicket}
              />
              <TabelaEquipe
                atendentes={equipeOrdenada}
                online={atendentesOnline}
                periodoLabel={periodoLabel}
                ordem={ordemEquipe}
                onOrdenar={ordenarEquipe}
                filtroAtendenteId={filtroAtendenteId}
                onFiltrar={(id) => setFiltroAtendenteId((prev) => (prev === id ? null : id))}
              />
            </div>
          </>
        )}
      </div>

      <Sheet open={!!ticketSelecionado} onOpenChange={(open) => !open && fecharDrawer()}>
        <SheetContent className="w-[380px] sm:max-w-[380px] overflow-y-auto">
          {ticketSelecionado && bucketSelecionado && (
            <TicketDrawer
              ticket={ticketSelecionado}
              bucket={bucketSelecionado}
              thresh={bucketSelecionado === 'pendente' ? THRESH_PENDENTE : THRESH_ATENDIMENTO}
              onAbrirConversa={abrirConversa}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const GrupoKpi: React.FC<{ titulo: string; live?: boolean; children: React.ReactNode }> = ({ titulo, live, children }) => (
  <Card className="p-2.5">
    <div className="flex items-center gap-1.5 px-0.5 mb-2 text-[10.5px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
      {live && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />}
      {titulo}
    </div>
    {children}
  </Card>
);

const KpiTile: React.FC<{ label: string; value: React.ReactNode; tone: Tom; sub: string; destaque?: boolean }> = ({ label, value, tone, sub, destaque }) => (
  <div className="rounded-lg bg-gray-50 dark:bg-slate-800/60 border border-gray-100 dark:border-slate-800 px-2 2xl:px-2.5 py-1.5 min-w-0">
    <div className="text-[10px] uppercase tracking-wide xl:tracking-normal 2xl:tracking-wide text-gray-400 font-semibold truncate" title={label}>{label}</div>
    <div className={`${destaque ? 'text-2xl' : 'text-xl'} font-bold tabular-nums leading-tight truncate ${TOM_TEXT[tone]}`}>{value}</div>
    <div className="text-[10px] text-gray-400 truncate" title={sub}>{sub}</div>
  </div>
);

const LegendaCor: React.FC<{ cor: string; label: string }> = ({ cor, label }) => (
  <div className="flex items-center gap-1.5">
    <span className={`h-2 w-2 rounded-full ${cor}`} />
    <span>{label}</span>
  </div>
);

const Painel: React.FC<{ titulo: string; contador: React.ReactNode; subtitulo: string; children: React.ReactNode }> = ({ titulo, contador, subtitulo, children }) => (
  <Card className="flex flex-col min-h-0 p-3">
    <div className="flex items-center gap-2 flex-none">
      <h2 className="font-bold text-sm">{titulo}</h2>
      <Badge variant="secondary" className="text-[10px]">{contador}</Badge>
    </div>
    <p className="text-[11px] text-gray-400 mb-2 flex-none truncate">{subtitulo}</p>
    <div className="flex-1 min-h-0 max-h-[480px] xl:max-h-none overflow-y-auto">
      {children}
    </div>
  </Card>
);

const ColunaTickets: React.FC<{
  titulo: string;
  subtitulo: string;
  tickets: PainelTicket[];
  bucket: Bucket;
  thresh: { warn: number; crit: number };
  onSelect: (ticket: PainelTicket, bucket: Bucket) => void;
}> = ({ titulo, subtitulo, tickets, bucket, thresh, onSelect }) => (
  <Painel titulo={titulo} contador={tickets.length} subtitulo={subtitulo}>
    <div className="flex flex-col gap-1.5 pr-1">
      {tickets.map((t) => (
        <TicketCard key={t.id} ticket={t} thresh={thresh} onClick={() => onSelect(t, bucket)} />
      ))}
      {tickets.length === 0 && (
        <div className="text-center text-xs text-gray-400 border border-dashed border-gray-200 dark:border-slate-700 rounded-lg py-6">
          Nenhum ticket por aqui.
        </div>
      )}
    </div>
  </Painel>
);

const CanalIcon: React.FC<{ canal: CanalTicket }> = ({ canal }) => {
  if (canal === 'META') return <Mail className="h-3 w-3" />;
  if (canal === 'DESCONHECIDO') return <Phone className="h-3 w-3" />;
  return <MessageCircle className="h-3 w-3" />;
};

const TicketCard: React.FC<{ ticket: PainelTicket; thresh: { warn: number; crit: number }; onClick: () => void }> = ({ ticket, thresh, onClick }) => {
  const elapsed = decorrido(ticket);
  const pausado = !!ticket.aguardandoClienteDesde;
  const urg = urgenciaTicket(ticket, thresh);
  return (
    <button
      onClick={onClick}
      className={`text-left w-full bg-gray-50 dark:bg-slate-700/40 hover:bg-gray-100 dark:hover:bg-slate-700 border-l-4 ${pausado ? 'border-l-sky-400' : URGENCY_BORDER[urg]} border border-gray-200 dark:border-slate-700 rounded-md px-2.5 py-1.5 transition-colors`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-[13px] truncate">{ticket.contato?.nome || ticket.contato?.telefone || 'Sem nome'}</span>
        <span className={`text-xs font-semibold tabular-nums flex items-center gap-1 flex-none ${pausado ? 'text-sky-600 dark:text-sky-400' : URGENCY_TEXT[urg]}`}>
          {pausado && <PauseCircle className="h-3 w-3" />}
          {!pausado && urg === 'crit' && <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />}
          {fmtElapsed(elapsed)}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1 min-w-0">
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded flex-none">
          <CanalIcon canal={ticket.canal} />
          {CANAL_LABEL[ticket.canal]}
        </span>
        {pausado && <SeloAguardandoCliente />}
        {ticket.assunto && <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{ticket.assunto}</span>}
        {ticket.atendente && (
          <span className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 flex-none">
            <Avatar className="h-4 w-4">
              <AvatarImage src={ticket.atendente.avatarUrl ? getFileUrl(ticket.atendente.avatarUrl) : undefined} />
              <AvatarFallback className="text-[8px]">{getInitials(ticket.atendente.nome)}</AvatarFallback>
            </Avatar>
            {ticket.atendente.nome.split(' ')[0]}
          </span>
        )}
      </div>
    </button>
  );
};

const COLUNAS_EQUIPE: { id: ColunaEquipe; label: string; title?: string; secundaria?: boolean }[] = [
  { id: 'chats', label: 'Chats', title: 'Em atendimento agora' },
  { id: 'pendentes', label: 'Pend.', title: 'Pendentes atribuídos', secundaria: true },
  { id: 'resolvidos', label: 'Resolv.', title: 'Resolvidos no período' },
  { id: 'total', label: 'Total', secundaria: true },
  { id: 'nota', label: 'Nota', title: 'Avaliação média no período' },
  { id: 'tme', label: 'TME', title: 'Tempo médio de espera' },
  { id: 'tma', label: 'TMA', title: 'Tempo médio de atendimento' }
];

type OrdemEquipe = { coluna: ColunaEquipe; desc: boolean };

// Colunas menos importantes somem em telas menores para a tabela caber sem scroll lateral
const SECUNDARIA = 'hidden 2xl:table-cell';

const CabecalhoEquipe: React.FC<{
  id: ColunaEquipe;
  label: string;
  title?: string;
  alinhar?: 'left' | 'right';
  className?: string;
  ordem: OrdemEquipe;
  onOrdenar: (coluna: ColunaEquipe) => void;
}> = ({ id, label, title, alinhar = 'right', className = '', ordem, onOrdenar }) => {
  const ativo = ordem.coluna === id;
  const Icone = ordem.desc ? ChevronDown : ChevronUp;
  return (
    <th
      title={title}
      onClick={() => onOrdenar(id)}
      className={`sticky top-0 z-10 bg-white dark:bg-zinc-950 px-2 py-1.5 font-semibold cursor-pointer select-none whitespace-nowrap hover:text-gray-600 dark:hover:text-gray-200 ${alinhar === 'right' ? 'text-right' : 'text-left'} ${ativo ? 'text-gray-700 dark:text-gray-200' : ''} ${className}`}
    >
      <span className={`inline-flex items-center gap-0.5 ${alinhar === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {ativo ? <Icone className="h-3 w-3" /> : <span className="w-3" />}
      </span>
    </th>
  );
};

// Une a antiga lista "Atendentes" e a tabela "Desempenho por Usuário" (mesmos dados) numa só tabela
const TabelaEquipe: React.FC<{
  atendentes: AtendenteCarga[];
  online: number;
  periodoLabel: string;
  ordem: OrdemEquipe;
  onOrdenar: (coluna: ColunaEquipe) => void;
  filtroAtendenteId: string | null;
  onFiltrar: (id: string) => void;
}> = ({ atendentes, online, periodoLabel, ordem, onOrdenar, filtroAtendenteId, onFiltrar }) => {
  return (
    <Painel
      titulo="Equipe"
      contador={`${online}/${atendentes.length} online`}
      subtitulo={`Clique numa linha para filtrar o quadro · chats ao vivo, demais colunas em ${periodoLabel.toLowerCase()}`}
    >
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wide text-gray-400">
          <tr className="border-b border-gray-200 dark:border-slate-700">
            <CabecalhoEquipe id="nome" label="Atendente" alinhar="left" ordem={ordem} onOrdenar={onOrdenar} />
            {COLUNAS_EQUIPE.map((c) => (
              <CabecalhoEquipe key={c.id} id={c.id} label={c.label} title={c.title} className={c.secundaria ? SECUNDARIA : ''} ordem={ordem} onOrdenar={onOrdenar} />
            ))}
          </tr>
        </thead>
        <tbody>
          {atendentes.map((a) => {
            const selecionado = filtroAtendenteId === a.id;
            return (
              <tr
                key={a.id}
                onClick={() => onFiltrar(a.id)}
                className={`border-b border-gray-100 dark:border-slate-800 cursor-pointer transition-colors ${
                  selecionado ? 'bg-sky-50 dark:bg-sky-500/10' : 'hover:bg-gray-50 dark:hover:bg-slate-800/60'
                }`}
              >
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="relative flex-none">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={a.avatarUrl ? getFileUrl(a.avatarUrl) : undefined} />
                        <AvatarFallback className="text-[9px]">{getInitials(a.nome)}</AvatarFallback>
                      </Avatar>
                      <span
                        title={STATUS_ATENDENTE_LABEL[a.status || 'offline']}
                        className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-white dark:border-slate-800 ${STATUS_ATENDENTE_DOT[a.status || 'offline']}`}
                      />
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate max-w-[180px]">{a.nome}</div>
                      <div className="text-[10px] text-gray-400 truncate max-w-[180px]" title={a.email}>{STATUS_ATENDENTE_LABEL[a.status || 'offline']}</div>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-1.5 text-right">
                  <span className={`inline-flex justify-center min-w-[1.5rem] rounded px-1 py-0.5 text-xs font-bold tabular-nums ${a.ticketsEmAtendimento > 0 ? 'bg-sky-500 text-white' : 'text-gray-400'}`}>{a.ticketsEmAtendimento}</span>
                </td>
                <td className={`px-2 py-1.5 text-right ${SECUNDARIA}`}>
                  <span className={`inline-flex justify-center min-w-[1.5rem] rounded px-1 py-0.5 text-xs font-bold tabular-nums ${a.pendentesAtribuidos > 0 ? 'bg-amber-400 text-white' : 'text-gray-400'}`}>{a.pendentesAtribuidos}</span>
                </td>
                <td className="px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{a.resolvidosPeriodo}</td>
                <td className={`px-2 py-1.5 text-right text-xs font-semibold tabular-nums ${SECUNDARIA}`}>{totalAtendente(a)}</td>
                <td className="px-2 py-1.5 text-right text-xs tabular-nums text-gray-600 dark:text-gray-300">
                  <span className="inline-flex items-center gap-0.5 justify-end">
                    {a.avaliacaoMedia !== null && <Star className="h-3 w-3 fill-amber-400 text-amber-400" />}
                    {fmtAvaliacao(a.avaliacaoMedia)}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right text-xs tabular-nums text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDuracaoSegundos(a.tmeSegundos)}</td>
                <td className="px-2 py-1.5 text-right text-xs tabular-nums text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDuracaoSegundos(a.tmaSegundos)}</td>
              </tr>
            );
          })}
          {atendentes.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center text-xs text-gray-400 py-6">Nenhum atendente ativo.</td>
            </tr>
          )}
        </tbody>
      </table>
    </Painel>
  );
};

const TicketDrawer: React.FC<{
  ticket: PainelTicket;
  bucket: Bucket;
  thresh: { warn: number; crit: number };
  onAbrirConversa: (ticket: PainelTicket) => void;
}> = ({ ticket, bucket, thresh, onAbrirConversa }) => {
  const elapsed = decorrido(ticket);
  const pausado = !!ticket.aguardandoClienteDesde;
  const urg = urgenciaTicket(ticket, thresh);
  const criado = new Date(ticket.dataCriacao).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const ultimaAtualizacao = new Date(ticket.statusAlteradoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="flex flex-col gap-5 mt-2">
      <SheetHeader>
        <SheetDescription className="text-xs tabular-nums">{ticket.protocolo}</SheetDescription>
        <SheetTitle>{ticket.contato?.nome || ticket.contato?.telefone}</SheetTitle>
      </SheetHeader>

      <div>
        <div className="flex items-center gap-2">
          <Badge variant={bucket === 'pendente' ? 'warning' : 'default'} className="uppercase text-[10px]">
            {bucket === 'pendente' ? 'Pendente' : bucket === 'aguardando' ? 'Aguardando retorno' : 'Em atendimento'}
          </Badge>
          {pausado && <SeloAguardandoCliente />}
        </div>
        <div className={`text-3xl font-bold tabular-nums mt-2 ${pausado ? 'text-sky-600 dark:text-sky-400' : URGENCY_TEXT[urg]}`}>
          {fmtElapsed(elapsed)}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          {bucket === 'pendente' ? 'tempo aguardando atribuição' : bucket === 'aguardando' ? 'tempo até o pedido de retorno' : 'tempo em atendimento'}
          {pausado && bucket !== 'aguardando' && ' · aguardando retorno do contato'}
        </div>
      </div>

      <Button variant="outline" className="justify-center gap-2" onClick={() => onAbrirConversa(ticket)}>
        <ExternalLink className="h-4 w-4" />
        Abrir conversa
      </Button>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Atendendo</div>
          {ticket.atendente ? (
            <div className="flex items-center gap-1.5">
              <Avatar className="h-5 w-5">
                <AvatarImage src={ticket.atendente.avatarUrl ? getFileUrl(ticket.atendente.avatarUrl) : undefined} />
                <AvatarFallback className="text-[9px]">{getInitials(ticket.atendente.nome)}</AvatarFallback>
              </Avatar>
              <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{ticket.atendente.nome}</span>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Sem atendente</p>
          )}
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Fila</div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{ticket.fila?.nome || 'Sem fila'}</p>
        </div>
        <div className="col-span-2">
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Última atualização</div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{ultimaAtualizacao}</p>
        </div>
      </div>

      {ticket.assunto && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Assunto</div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{ticket.assunto}</p>
        </div>
      )}

      <div>
        <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Ticket criado</div>
        <p className="text-sm text-gray-600 dark:text-gray-300">{criado} · {CANAL_LABEL[ticket.canal]}</p>
      </div>

    </div>
  );
};

export default PainelOperacoesPage;
